import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Edit, Heart, Camera } from 'lucide-react';
import { DateTime } from 'luxon';
import { useAxios } from '../../core/_axios';

interface CampaignData {
  service: {
    unique_identifier: string;
    public_data: {
      deceased_person?: {
        full_name: string;
        preferred_name?: string;
        birth_date?: string;
        death_date?: string;
        avatar_url?: string;
      };
    };
  };
  signature: {
    unique_identifier: string;
    public_data: any;
  };
  signer_info: {
    name: string;
    email: string;
    phone?: string;
    relationship?: string;
    address?: string;
  };
  account: {
    name: string;
  };
  existing_memories?: Array<{
    id: string;
    memory_text: string;
    photos: Array<{ url: string; uploaded_at?: string }>;
    created_at: string;
  }>;
}

export const ShareMemory: React.FC = () => {
  const { campaignTrackingId } = useParams<{ campaignTrackingId: string }>();
  const navigate = useNavigate();
  const axios = useAxios();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [memory, setMemory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [memoryShared, setMemoryShared] = useState(false);
  const [editingSignature, setEditingSignature] = useState(false);
  const [editedSignerInfo, setEditedSignerInfo] = useState<typeof signer_info | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [existingMemory, setExistingMemory] = useState<string>('');
  const [hasSharedMemory, setHasSharedMemory] = useState(false);
  const [sessionMemoryShared, setSessionMemoryShared] = useState(false);
  const [viewingPhotoUrl, setViewingPhotoUrl] = useState<string | null>(null);
  const [isNewSigner, setIsNewSigner] = useState(false);

  useEffect(() => {
    fetchCampaignData();
  }, [campaignTrackingId]);

  const fetchCampaignData = async () => {
    try {
      const response = await axios.get(`/api/v1/app/anon/memory/${campaignTrackingId}`);
      
      if (response.data.success) {
        setCampaignData(response.data.data);
        
        // Check for existing memories
        if (response.data.data.existing_memories && response.data.data.existing_memories.length > 0) {
          const memories = response.data.data.existing_memories;
          
          // Find memory with text
          const textMemory = memories.find((m: any) => m.memory_text && m.memory_text.trim() !== '');
          if (textMemory) {
            setExistingMemory(textMemory.memory_text);
            // Don't set the memory field - let user add a new one
            setHasSharedMemory(true);
          }
          
          // Collect all photos from all memories
          const allPhotos = memories.flatMap((m: any) => 
            (m.photos || []).map((p: any) => p.url)
          );
          setUploadedPhotos(allPhotos);
        }
      } else {
        setError('Invalid or expired link');
      }
    } catch (err) {
      console.error('Error fetching campaign data:', err);
      setError('Unable to load memory page');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSignature = () => {
    if (!editingSignature && campaignData) {
      setEditedSignerInfo({...campaignData.signer_info});
    }
    setEditingSignature(!editingSignature);
  };
  
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSaveSignatureEdit = async () => {
    if (!editedSignerInfo || !campaignData || !campaignTrackingId) return;
    
    // Validate that name is provided for new signers
    if (isNewSigner && !editedSignerInfo.name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    // Validate email if provided
    if (editedSignerInfo.email && !validateEmail(editedSignerInfo.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setUploading(true);
    try {
      const response = await axios.post(`/api/v1/app/anon/memory/${campaignTrackingId}/update-signature`, {
        signer_info: {
          ...editedSignerInfo,
          is_new_person: isNewSigner // Flag to backend to create new signature
        }
      });
      
      if (response.data.success) {
        // If a new signature was created, update the campaign tracking ID
        if (response.data.data.new_signature_id) {
          // The backend created a new signature and campaign
          // We should update our local state to reflect this
          setCampaignData({
            ...campaignData,
            signer_info: editedSignerInfo,
            signature: {
              ...campaignData.signature,
              unique_identifier: response.data.data.new_signature_id
            }
          });
        } else {
          setCampaignData({
            ...campaignData,
            signer_info: editedSignerInfo
          });
        }
        
        setEditingSignature(false);
        setError(null);
        
        // If this was a new signer, reset the flag since they've now saved their info
        if (isNewSigner) {
          setIsNewSigner(false);
          // Clear the "has shared memory" flag since this is a new person
          setHasSharedMemory(false);
          setExistingMemory('');
        }
      } else {
        setError('Failed to update information. Please try again.');
      }
    } catch (err) {
      console.error('Error updating signature:', err);
      setError('Unable to update information. Please try again later.');
    } finally {
      setUploading(false);
    }
  };
  
  const handleCancelEdit = () => {
    // If this is a new signer, they need to provide their info
    if (isNewSigner) {
      // Reset to show original signer's info
      setIsNewSigner(false);
      setHasSharedMemory(true);
      setEditingSignature(false);
      setEditedSignerInfo(null);
      setError(null);
    } else {
      setEditingSignature(false);
      setEditedSignerInfo(null);
      setError(null);
    }
  };

  const handleUploadPhoto = () => {
    if (!showPhotoModal) {
      fileInputRef.current?.click();
    }
  };
  
  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Could not create blob'));
              }
            },
            file.type,
            0.85 // 85% quality
          );
        };
        img.onerror = () => reject(new Error('Could not load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validFiles: File[] = [];
    const previews: string[] = [];
    
    setError(null);
    setUploading(true);
    
    try {
      for (const file of Array.from(files)) {
        if (!allowedTypes.includes(file.type)) {
          setError('Please upload valid image files (JPEG, PNG, or GIF)');
          setUploading(false);
          return;
        }
        
        let processedFile = file;
        
        // If file is larger than 10MB, resize it
        if (file.size > 10 * 1024 * 1024) {
          try {
            const resizedBlob = await resizeImage(file, 2048, 2048);
            processedFile = new File([resizedBlob], file.name, { type: file.type });
          } catch (err) {
            console.error('Error resizing image:', err);
            setError('Could not process large image. Please try a smaller file.');
            setUploading(false);
            return;
          }
        }
        
        validFiles.push(processedFile);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result as string);
          if (previews.length === validFiles.length) {
            setPreviewUrls(previews);
            setSelectedFiles(validFiles);
            setShowPhotoModal(true);
            setUploading(false);
          }
        };
        reader.readAsDataURL(processedFile);
      }
    } catch (err) {
      console.error('Error processing files:', err);
      setError('Could not process images. Please try again.');
      setUploading(false);
    }
  };
  
  const handleCancelUpload = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    setUploadProgress(0);
    setCurrentUploadIndex(0);
    setShowPhotoModal(false);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleConfirmUpload = async () => {
    if (selectedFiles.length === 0 || !campaignTrackingId) return;
    
    setUploading(true);
    setError(null);
    setCurrentUploadIndex(0);
    
    const newPhotos: string[] = [];
    
    for (let i = 0; i < selectedFiles.length; i++) {
      setCurrentUploadIndex(i);
      setUploadProgress(0);
      
      try {
        const formData = new FormData();
        formData.append('photo', selectedFiles[i]);
        
        const response = await axios.post(`/api/v1/app/anon/memory/${campaignTrackingId}/upload-photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress(percentCompleted);
          }
        });
        
        if (response.data.success) {
          newPhotos.push(response.data.data.photo_url);
        } else {
          setError(`Failed to upload photo ${i + 1}. Please try again.`);
        }
      } catch (err) {
        console.error(`Error uploading photo ${i + 1}:`, err);
        setError(`Unable to upload photo ${i + 1}. Please try again later.`);
      }
    }
    
    if (newPhotos.length > 0) {
      setUploadedPhotos([...uploadedPhotos, ...newPhotos]);
    }
    
    handleCancelUpload();
    setUploading(false);
  };

  const handleShareMemory = async () => {
    if (!memory.trim() || !campaignTrackingId) return;
    
    setUploading(true);
    try {
      const response = await axios.post(`/api/v1/app/anon/memory/${campaignTrackingId}`, {
        memory_text: memory.trim()
      });
      
      if (response.data.success) {
        setMemoryShared(true);
        setSessionMemoryShared(true);
        setHasSharedMemory(true);
        setExistingMemory(memory.trim());
      } else {
        setError('Failed to save your memory. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting memory:', err);
      setError('Unable to save your memory. Please try again later.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <i className="ki-filled ki-information-circle text-3xl text-amber-600"></i>
            </div>
          </div>
          <h2 className="text-2xl font-semibold mb-3">Something Went Wrong</h2>
          <p className="text-gray-600 mb-6">{error || 'This link may be invalid or expired.'}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { service, signature, signer_info, account } = campaignData;
  const deceased = service.public_data.deceased_person;
  const deceasedName = deceased?.full_name || 'your loved one';
  const deceasedFirstName = deceased?.preferred_name || deceased?.full_name?.split(' ')[0] || 'your loved one';
  
  const handleShareAsNewPerson = () => {
    // Clear existing memory state
    setHasSharedMemory(false);
    setExistingMemory('');
    setUploadedPhotos([]);
    setMemory('');
    setSessionMemoryShared(false);
    setIsNewSigner(true);
    
    // Clear signer info to allow new person to enter their details
    setEditingSignature(true);
    setEditedSignerInfo({
      name: '',
      email: '',
      phone: '',
      relationship: '',
      address: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="w-full max-w-6xl mx-auto px-4 py-3">
          <h1 className="text-xl font-semibold text-gray-900">Share a Memory</h1>
        </div>
      </div>
      
      {/* Identity Banner - Show when there's an existing memory and not a new signer */}
      {hasSharedMemory && !sessionMemoryShared && !isNewSigner && signer_info.name && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="w-full max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="ki-filled ki-information-circle text-amber-600 text-lg mr-2"></i>
                <div className="text-sm">
                  <span className="text-amber-900">This memory link was sent to:</span>
                  <span className="text-amber-900 font-medium ml-1">{signer_info.name}</span>
                </div>
              </div>
              <button
                onClick={handleShareAsNewPerson}
                className="btn btn-sm btn-light-warning"
              >
                <i className="ki-filled ki-user-plus mr-1"></i>
                Not you? Share your own memory
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="w-full max-w-6xl mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto">
        {/* Service Info Card - Compact */}
        <div className="bg-white rounded-lg shadow-sm p-3 mb-2">
          <div className="flex items-center gap-3">
            {deceased?.avatar_url ? (
              <img
                src={deceased.avatar_url}
                alt={deceased.full_name}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <i className="ki-filled ki-user text-xl text-gray-400"></i>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold truncate">{deceased?.full_name || 'Memorial'}</h2>
              {deceased?.birth_date && deceased?.death_date && (
                <p className="text-xs text-gray-600">
                  {DateTime.fromISO(deceased.birth_date).toFormat('LLL d, yyyy')} - {DateTime.fromISO(deceased.death_date).toFormat('LLL d, yyyy')}
                </p>
              )}
              <p className="text-xs text-gray-500">{account.name}</p>
            </div>
          </div>
        </div>

        {/* Your Signature Info - Collapsible, Compact */}
        <details open={editingSignature || isNewSigner} className={`bg-gray-50 ${hasSharedMemory && !sessionMemoryShared && existingMemory ? 'rounded-t-lg rounded-b-none' : 'rounded-lg mb-1'}`}>
          <summary className="cursor-pointer p-2 flex items-center justify-between hover:bg-gray-100 rounded-lg">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <i className="ki-filled ki-user-tick text-gray-600 text-sm flex-shrink-0"></i>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-700 truncate">
                  {isNewSigner && !editedSignerInfo?.name ? 'Enter Your Information' : signer_info.name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {signer_info.phone && `${signer_info.phone} • `}{signer_info.email || 'No email provided'}
                </div>
              </div>
            </div>
            <i className="ki-outline ki-down text-gray-400 text-xs flex-shrink-0"></i>
          </summary>
          <div className="p-2">
            {editingSignature && editedSignerInfo ? (
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveSignatureEdit();
                }}
                className="space-y-2"
              >
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editedSignerInfo.name}
                    onChange={(e) => setEditedSignerInfo({...editedSignerInfo, name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editedSignerInfo.email || ''}
                    onChange={(e) => {
                      setEditedSignerInfo({...editedSignerInfo, email: e.target.value});
                      // Clear error if it was an email validation error
                      if (error === 'Please enter a valid email address') {
                        setError(null);
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your@email.com (optional)"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editedSignerInfo.phone || ''}
                    onChange={(e) => setEditedSignerInfo({...editedSignerInfo, phone: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567 (optional)"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Relationship</label>
                  <input
                    type="text"
                    value={editedSignerInfo.relationship || ''}
                    onChange={(e) => setEditedSignerInfo({...editedSignerInfo, relationship: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Friend, Family, etc. (optional)"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Address</label>
                  <textarea
                    value={editedSignerInfo.address || ''}
                    onChange={(e) => setEditedSignerInfo({...editedSignerInfo, address: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={2}
                    placeholder="Your address (optional)"
                  />
                </div>
                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">{error}</div>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={uploading}
                    className="btn btn-sm btn-primary flex-1"
                  >
                    {uploading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={uploading}
                    className="btn btn-sm btn-light flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex items-start justify-between px-1">
                <div className="text-xs space-y-1">
                  <p className="text-gray-600">
                    <span className="font-medium">Email:</span> {signer_info.email}
                  </p>
                  {signer_info.phone && (
                    <p className="text-gray-600">
                      <span className="font-medium">Phone:</span> {signer_info.phone}
                    </p>
                  )}
                  {signer_info.relationship && (
                    <p className="text-gray-600">
                      <span className="font-medium">Relationship:</span> {signer_info.relationship}
                    </p>
                  )}
                  {signer_info.address && (
                    <p className="text-gray-600">
                      <span className="font-medium">Address:</span> {signer_info.address}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleEditSignature}
                  className="btn btn-sm btn-light ml-3 text-xs"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </button>
              </div>
            )}
          </div>
        </details>

        {/* Show previous memory if exists and not in current session */}
        {hasSharedMemory && !sessionMemoryShared && existingMemory && (
          <div className="bg-gray-50 rounded-t-none rounded-b-lg p-2 mb-2 border-t border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600 mb-1">Your Previous Memory:</p>
                <p className="text-sm text-gray-700 italic whitespace-pre-wrap">&ldquo;{existingMemory}&rdquo;</p>
              </div>
            </div>
          </div>
        )}

        {/* Share Memory Section - Primary Focus */}
        <div className="bg-white rounded-lg shadow-lg border-2 border-blue-200 p-4 mb-4">
          {!sessionMemoryShared ? (
            <>
              <h3 className="text-base font-semibold mb-2 flex items-center text-blue-900">
                <Heart className="w-4 h-4 mr-2 text-red-500" />
                {hasSharedMemory && !sessionMemoryShared ? `Share Another Memory of ${deceasedFirstName}` : `Share a Memory of ${deceasedFirstName}`}
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                {hasSharedMemory && !sessionMemoryShared ? 
                  `You previously shared a memory. Feel free to add another one!` : 
                  `Share a favorite memory, story, or message. Your words will comfort the family.`
                }
              </p>
              <textarea
                id="memory-textarea"
                value={memory}
                onChange={(e) => {
                  const text = e.target.value;
                  if (text.length <= 1000) {
                    setMemory(text);
                  }
                }}
                placeholder={hasSharedMemory && !sessionMemoryShared ? "Share another memory..." : "I remember when..."}
                className="form-control w-full text-base p-3 border-2 hover:border-blue-300 focus:border-blue-400 transition-colors min-h-[120px] resize-none"
                rows={5}
                autoFocus
                disabled={uploading}
                maxLength={1000}
              />
              {error && (
                <div className="mt-2 text-xs text-red-600">{error}</div>
              )}
              <div className="mt-3 flex items-center justify-between">
                <p className={`text-xs ${memory.length > 900 ? 'text-amber-600' : 'text-gray-500'}`}>
                  {memory.length > 0 ? `${memory.length} / 1000 characters${memory.length > 900 ? ' (approaching limit)' : ''}` : 'Start typing to share your memory'}
                </p>
                <button
                  onClick={handleShareMemory}
                  disabled={!memory.trim() || uploading}
                  className="btn btn-primary"
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      Sharing...
                    </>
                  ) : (
                    <>
                      <Heart className="w-4 h-4 mr-2" />
                      Share Memory
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-3">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mb-2">
                <i className="ki-filled ki-check text-xl text-green-600"></i>
              </div>
              <h3 className="text-sm font-semibold mb-1">Thank You for Sharing</h3>
              <p className="text-xs text-gray-600 mb-2">
                Your memory of {deceasedFirstName} has been saved.
              </p>
              <div className="bg-gray-50 rounded-lg p-2 text-left mb-2">
                <p className="text-xs text-gray-700 italic whitespace-pre-wrap">&ldquo;{memory || existingMemory}&rdquo;</p>
              </div>
              <p className="text-xs text-gray-500">
                Consider adding photos below to preserve more memories
              </p>
            </div>
          )}
        </div>

        {/* Upload Photos Section - Clickable */}
        <div 
          className={`bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-all ${
            (memoryShared || hasSharedMemory) ? 'border-2 border-blue-200' : ''
          } ${uploading || showPhotoModal ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={uploading || showPhotoModal ? undefined : handleUploadPhoto}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-base font-semibold flex items-center">
                <Camera className="w-4 h-4 mr-2 text-blue-500" />
                {uploading ? 'Uploading...' : (memoryShared || hasSharedMemory) ? 'Add Photos to Your Memory' : 'Share Photos'}
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                {uploading && !showPhotoModal
                  ? `Processing images...`
                  : uploading
                  ? `Uploading photo ${currentUploadIndex + 1} of ${selectedFiles.length}...`
                  : (memoryShared || hasSharedMemory) 
                  ? `Photos help preserve precious memories of ${deceasedFirstName}.`
                  : `Click to upload photos of ${deceasedFirstName} for the family.`
                }
              </p>
              {error && (error.includes('photo') || error.includes('image')) && (
                <p className="text-xs text-red-600 mt-1">{error}</p>
              )}
            </div>
            {uploading ? (
              <span className="spinner-border spinner-border-sm text-gray-400" role="status" aria-hidden="true"></span>
            ) : (
              <i className="ki-outline ki-right text-gray-400"></i>
            )}
          </div>
        </div>

        {/* Photo upload modal */}
        {showPhotoModal && selectedFiles.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">
                  {uploading ? 
                    `Uploading Photo ${currentUploadIndex + 1} of ${selectedFiles.length}` : 
                    `Preview Photos (${selectedFiles.length})`
                  }
                </h3>
              </div>
              
              <div className="p-4">
                {selectedFiles.length === 1 ? (
                  <div className="relative">
                    <img 
                      src={previewUrls[0]} 
                      alt="Preview" 
                      className="w-full max-h-96 object-contain rounded-lg bg-gray-100"
                    />
                    {uploading && (
                      <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center rounded-lg">
                        <div className="w-32">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-600 transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <p className="text-sm text-gray-600 mt-2 text-center">{uploadProgress}% uploaded</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {previewUrls.map((url, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={url} 
                          alt={`Preview ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-lg bg-gray-100"
                        />
                        {uploading && index === currentUploadIndex && (
                          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg">
                            <div className="text-center">
                              <div className="spinner-border spinner-border-sm text-primary mb-1" />
                              <p className="text-xs">{uploadProgress}%</p>
                            </div>
                          </div>
                        )}
                        {uploading && index < currentUploadIndex && (
                          <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center rounded-lg">
                            <i className="ki-filled ki-check text-2xl text-green-600"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {error && (
                  <div className="mt-3 text-sm text-red-600">{error}</div>
                )}
              </div>
              
              <div className="p-4 border-t flex gap-2">
                <button
                  onClick={handleConfirmUpload}
                  disabled={uploading}
                  className="btn btn-primary flex-1"
                >
                  {uploading ? 
                    `Uploading ${currentUploadIndex + 1}/${selectedFiles.length}...` : 
                    `Upload ${selectedFiles.length} Photo${selectedFiles.length > 1 ? 's' : ''}`
                  }
                </button>
                <button
                  onClick={handleCancelUpload}
                  disabled={uploading}
                  className="btn btn-light"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Display uploaded photos */}
        {uploadedPhotos.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 mt-4">
            <h3 className="text-sm font-semibold mb-2">Uploaded Photos ({uploadedPhotos.length})</h3>
            <div className="grid grid-cols-3 gap-2">
              {uploadedPhotos.map((photoUrl, index) => (
                <div 
                  key={index} 
                  className="aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setViewingPhotoUrl(photoUrl)}
                >
                  <img 
                    src={photoUrl} 
                    alt={`Memory photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photo viewer modal */}
        {viewingPhotoUrl && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50"
            onClick={() => setViewingPhotoUrl(null)}
          >
            <div className="relative max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center">
              <img 
                src={viewingPhotoUrl} 
                alt="Full size photo"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Navigation buttons - only show if more than 1 photo */}
              {uploadedPhotos.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const currentIndex = uploadedPhotos.indexOf(viewingPhotoUrl);
                      const prevIndex = currentIndex === 0 ? uploadedPhotos.length - 1 : currentIndex - 1;
                      setViewingPhotoUrl(uploadedPhotos[prevIndex]);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 btn btn-light btn-sm rounded-full w-10 h-10 p-0 flex items-center justify-center"
                    aria-label="Previous photo"
                  >
                    <i className="ki-filled ki-left text-xl"></i>
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const currentIndex = uploadedPhotos.indexOf(viewingPhotoUrl);
                      const nextIndex = (currentIndex + 1) % uploadedPhotos.length;
                      setViewingPhotoUrl(uploadedPhotos[nextIndex]);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 btn btn-light btn-sm rounded-full w-10 h-10 p-0 flex items-center justify-center"
                    aria-label="Next photo"
                  >
                    <i className="ki-filled ki-right text-xl"></i>
                  </button>
                  
                  {/* Photo counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                    {uploadedPhotos.indexOf(viewingPhotoUrl) + 1} / {uploadedPhotos.length}
                  </div>
                </>
              )}
              
              <button
                onClick={() => setViewingPhotoUrl(null)}
                className="absolute top-4 right-4 btn btn-light btn-sm rounded-full w-10 h-10 p-0 flex items-center justify-center"
                aria-label="Close"
              >
                <i className="ki-filled ki-cross text-xl"></i>
              </button>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>This memorial page is provided by SignatureGuestbook</p>
        </div>
        </div>
      </div>
    </div>
  );
};